import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Users, Star, Calendar, TrendingUp } from 'lucide-react';
import { useSiteData } from '../contexts/SiteDataContext';

const StatsCounter = () => {
  const { stats: statsData } = useSiteData();
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const [counters, setCounters] = useState({
    readers: 0,
    reviews: 0,
    events: 0,
    rating: 0,
  });

  useEffect(() => {
    if (inView) {
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setCounters({
          readers: Math.floor(statsData.readers * progress),
          reviews: Math.floor(statsData.reviews * progress),
          events: Math.floor(statsData.events * progress),
          rating: (statsData.rating * progress).toFixed(1),
        });

        if (currentStep >= steps) {
          clearInterval(interval);
          setCounters({
            readers: statsData.readers,
            reviews: statsData.reviews,
            events: statsData.events,
            rating: statsData.rating,
          });
        }
      }, stepDuration);

      return () => clearInterval(interval);
    }
  }, [inView, statsData]);

  const stats = [
    {
      icon: Users,
      value: counters.readers.toLocaleString(),
      label: 'Readers',
      labelHindi: 'पाठक',
      color: 'from-orange-500 to-orange-600',
    },
    {
      icon: Star,
      value: counters.reviews.toLocaleString(),
      label: 'Reviews',
      labelHindi: 'समीक्षाएं',
      color: 'from-blue-500 to-blue-600',
    },
    {
      icon: Calendar,
      value: counters.events.toLocaleString(),
      label: 'Events',
      labelHindi: 'कार्यक्रम',
      color: 'from-orange-600 to-red-600',
    },
    {
      icon: TrendingUp,
      value: counters.rating,
      label: 'Rating',
      labelHindi: 'रेटिंग',
      color: 'from-green-500 to-green-600',
      suffix: '/5',
    },
  ];

  return (
    <section ref={ref} className="py-16 bg-gradient-to-br from-blue-900 via-blue-950 to-blue-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-72 h-72 bg-orange-400 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-orange-400 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="text-center"
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-2xl bg-white/10 backdrop-blur-sm"
              >
                <stat.icon className="h-8 w-8 text-orange-400" />
              </motion.div>
              
              <motion.div 
                className={`text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r ${stat.color} text-transparent bg-clip-text`}
              >
                {stat.value}{stat.suffix}
              </motion.div>
              
              <div className="text-white font-semibold text-lg" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>{stat.labelHindi}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsCounter;